'use client';

import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import clsx from 'clsx';
import { AnimatedText } from './AnimatedText';
import { NoiseOverlay } from './NoiseOverlay';

interface PreloaderProps {
  onComplete: () => void;
}

export const Preloader: React.FC<PreloaderProps> = ({ onComplete }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const vignetteRef = useRef<HTMLDivElement>(null); // 1. ADD THIS REF
  
  const textRef = useRef<SVGSVGElement>(null);
  const fillMaskRef = useRef<SVGRectElement>(null);
  const glowRef = useRef<SVGFEGaussianBlurElement>(null);
  const edgeBlurRef = useRef<SVGFEGaussianBlurElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
  defaults: { ease: 'power3.out' }, // slightly faster feel
  onComplete: () => {
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    onComplete();
  }
});

// faster sequence
tl.to(fillMaskRef.current, {
  x: '0%',
  duration: 1.2,
}, 0);

tl.to(edgeBlurRef.current, {
  attr: { stdDeviation: '0 0' },
  duration: 0.8,
}, 0.4);

tl.set('#shine-overlay', { opacity: 1 }, '+=0.1');

tl.to(glowRef.current, {
  attr: { stdDeviation: '10' },
  duration: 0.25,
});

tl.to('#shine-overlay', {
  x: '100%',
  duration: 0.6,
}, '-=0.1');

tl.to(glowRef.current, {
  attr: { stdDeviation: '0' },
  duration: 0.3,
}, '-=0.3');

tl.to(textRef.current, {
  opacity: 0,
  scale: 0.98,
  duration: 0.3,
}, '+=0.05');

tl.to(vignetteRef.current, {
  opacity: 0,
  duration: 0.4,
}, '-=0.3');

tl.to(leftPanelRef.current, {
  x: '-100%',
  duration: 0.7,
}, '-=0.4');

tl.to(rightPanelRef.current, {
  x: '100%',
  duration: 0.7,
}, '-=0.7');

tl.to(containerRef.current, {
  opacity: 0,
  display: 'none',
  duration: 0.05
});


    }, containerRef);

    return () => ctx.revert();
  }, [onComplete]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-transparent select-none"
    >
      {/* Cinematic Vignette Shield — ATTACHED REF HERE */}
      <div 
        ref={vignetteRef}
        className="absolute inset-0 z-30 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.85)_100%)] will-change-[opacity]" 
      />
      
      {/* Film Grain Mesh */}
      <NoiseOverlay />

      {/* Symmetric Luxury Splitting Portals */}
      <div 
        ref={leftPanelRef} 
        className="absolute top-0 bottom-0 left-0 w-1/2 bg-[#000000] z-10 will-change-transform" 
      />
      <div 
        ref={rightPanelRef} 
        className="absolute top-0 bottom-0 right-0 w-1/2 bg-[#000000] z-10 will-change-transform" 
      />

      {/* Typography Core Container */}
      <div className="relative z-20 flex items-center justify-center w-full h-full">
        <AnimatedText
          textRef={textRef}
          fillMaskRef={fillMaskRef}
          glowRef={glowRef}
          edgeBlurRef={edgeBlurRef}
        />
      </div>
    </div>
  );
};